import {
  ConversationParticipantEntity,
  isSendableSmsLineType,
} from '@provision/conversation-types';
import { toNationalPhoneDigits } from '@provision/formatting-utils';
import { formatToPhoneNumber } from 'src/components/FormattedPhoneInput/format-to-phone-number';
import type { ConversationParticipantItem } from 'src/graphql/entities/conversation-participants';
import type { SmsSessionRecipientInput } from './sms-session.operations';

/** one TwilioSMS channel of the conversation and the person behind it */
export interface SmsChannelInfo {
  conversationChannelId: string;
  participantId: string;
  name: string | null;
  /** the label of the client phone row with these digits, when someone on the conversation has one */
  label: string | null;
  digits: string;
  internalIdentifier: string;
  entity: ConversationParticipantEntity;
  identity: string;
  lineType: string | null;
  lineTypeCheckedAt: string | null;
}

/** a number the composer texts; `digits` are the 10 national digits every channel row carries */
export type SmsRecipient = {
  digits: string;
  name: string | null;
  participantId: string | null;
} & (
  | { kind: 'channel'; conversationChannelId: string }
  | { kind: 'clientPhone'; clientPhoneNumberId: string; label: string | null }
  | { kind: 'typed' }
);

export interface SmsNumberVerdict {
  lineType: string | null;
  lineTypeCheckedAt: string | null;
  smsOptOut: boolean;
}

export interface SmsNumberOption {
  recipient: SmsRecipient;
  verdict: SmsNumberVerdict;
}

export interface SmsSessionDescription {
  single: { name: string | null; number: string } | null;
  group: string | null;
}

export function toRecipientInput(recipient: SmsRecipient): SmsSessionRecipientInput {
  switch (recipient.kind) {
    case 'channel':
      return { conversationChannelId: recipient.conversationChannelId };
    case 'clientPhone':
      return { clientPhoneNumberId: recipient.clientPhoneNumberId };
    case 'typed':
      return { phoneNumber: recipient.digits };
  }
}

/** what the menu says about a number; disabled keeps it from being picked */
export function getNumberStatus(verdict: SmsNumberVerdict): {
  disabled: boolean;
  note: string | null;
} {
  if (verdict.smsOptOut) return { disabled: true, note: 'Opted out' };
  if (!verdict.lineTypeCheckedAt) return { disabled: false, note: 'Unverified' };
  if (!isSendableSmsLineType(verdict.lineType)) {
    return {
      disabled: true,
      note:
        verdict.lineType === 'landline'
          ? 'Landline'
          : `Can't receive texts (${verdict.lineType})`,
    };
  }
  return { disabled: false, note: null };
}

/** every number the people on the conversation can be texted on, with its deliverability verdict */
export function buildNumberOptions({
  participants,
  channels,
  labels,
  checked,
}: {
  participants: ConversationParticipantItem[];
  channels: readonly SmsChannelInfo[];
  labels: { id: number; label: string }[];
  checked: ReadonlyMap<string, SmsNumberVerdict>;
}): SmsNumberOption[] {
  const channelByDigits = new Map(channels.map((channel) => [channel.digits, channel]));
  const options: SmsNumberOption[] = [];
  const covered = new Set<string>();

  for (const participant of participants) {
    const client = participant.client;
    if (!client) continue;
    for (const row of client.phoneNumbers ?? []) {
      const digits =
        row.number === undefined ? null : toNationalPhoneDigits(String(row.number));
      // one option per number: a second row with the same digits adds nothing a chip could pick
      if (!digits || !row.clientPhoneNumberId || covered.has(digits)) continue;
      covered.add(digits);
      const stamp = checked.get(digits) ?? channelByDigits.get(digits);
      options.push({
        recipient: {
          kind: 'clientPhone',
          digits,
          clientPhoneNumberId: row.clientPhoneNumberId,
          participantId: participant.conversationParticipantId,
          name: client.fullName,
          label: labels.find((label) => label.id === row.labelId)?.label ?? null,
        },
        verdict: {
          lineType: stamp?.lineType ?? null,
          lineTypeCheckedAt: stamp?.lineTypeCheckedAt ?? null,
          smsOptOut: Boolean(row.smsOptOut),
        },
      });
    }
  }

  for (const channel of channels) {
    if (covered.has(channel.digits)) continue;
    covered.add(channel.digits);
    const stamp = checked.get(channel.digits) ?? channel;
    options.push({
      recipient: toChannelRecipient(channel),
      verdict: {
        lineType: stamp.lineType,
        lineTypeCheckedAt: stamp.lineTypeCheckedAt,
        smsOptOut: false,
      },
    });
  }

  return options;
}

/** the oldest SMS channel, else the requester's primary row, else their first row */
export function defaultSmsRecipients({
  options,
  channels,
  participants,
  requesterId,
}: {
  options: SmsNumberOption[];
  channels: readonly SmsChannelInfo[];
  participants: ConversationParticipantItem[];
  requesterId: string | null;
}): SmsRecipient[] {
  const [oldest] = channels;
  if (oldest) {
    const option = options.find((candidate) => candidate.recipient.digits === oldest.digits);
    return option ? [option.recipient] : [];
  }

  const client = participants.find((p) => p.conversationParticipantId === requesterId)?.client;
  if (!client) return [];
  const rows = options.filter(
    (candidate) =>
      candidate.recipient.kind === 'clientPhone' &&
      candidate.recipient.participantId === requesterId,
  );
  const primary = rows.find(
    (candidate) =>
      candidate.recipient.kind === 'clientPhone' &&
      candidate.recipient.clientPhoneNumberId === client.primaryClientPhoneNumberId,
  );
  const pick = primary ?? rows[0];
  return pick ? [pick.recipient] : [];
}

/** a session's recipients by channel id; ids the map does not know yet are left out */
export function sessionRecipients(
  channelIds: readonly string[],
  options: SmsNumberOption[],
  channelsById: ReadonlyMap<string, SmsChannelInfo>,
): SmsRecipient[] {
  return channelIds.flatMap((channelId) => {
    const channel = channelsById.get(channelId);
    if (!channel) return [];
    const option = options.find((candidate) => candidate.recipient.digits === channel.digits);
    return [option?.recipient ?? toChannelRecipient(channel)];
  });
}

/** how a session is named in the header and on the ended card; ids the map does not know yet count as "N more" */
export function describeSession(
  channelIds: readonly string[],
  channelsById: ReadonlyMap<string, SmsChannelInfo>,
): SmsSessionDescription {
  const known = channelIds.flatMap((id) => channelsById.get(id) ?? []);
  if (!known.length) return { single: null, group: null };
  if (channelIds.length === 1) {
    return {
      single: { name: known[0].name, number: formatToPhoneNumber(known[0].digits) },
      group: null,
    };
  }
  const names = known.map((channel) => channel.name ?? formatToPhoneNumber(channel.digits));
  const unknown = channelIds.length - known.length;
  return {
    single: null,
    group: `group text with ${joinNames(unknown ? [...names, `${unknown} more`] : names)}`,
  };
}

export function joinNames(names: string[]): string {
  if (names.length <= 1) return names[0] ?? '';
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

function toChannelRecipient(channel: SmsChannelInfo): SmsRecipient {
  return {
    kind: 'channel',
    digits: channel.digits,
    conversationChannelId: channel.conversationChannelId,
    participantId: channel.participantId,
    name: channel.name,
  };
}
